import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Check, X, Loader2, Building2, Package, Calendar, MapPin } from 'lucide-react'

export default function ApproveOrder() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [ctx, setCtx] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [outcome, setOutcome] = useState(null) // 'approved' | 'rejected'
  const [reason, setReason] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const { data, error } = await supabase.rpc('get_order_for_approval', { p_token: token })
        if (cancelled) return
        if (error) throw error
        if (!data?.ok) throw new Error(data?.error || 'Could not load this order')
        setCtx(data)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Something went wrong')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [token])

  const brandColour = ctx?.brand?.primary_colour || '#0a5842'
  const logoUrl = ctx?.brand?.logo_url

  const total = (ctx?.lines || []).reduce((s, l) => s + Number(l.subtotal || 0), 0)
  const fmtMoney = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
  const fmtQty = (n) => {
    const v = Math.round(Number(n || 0) * 100) / 100
    return Number.isInteger(v) ? v.toLocaleString('en-GB') : v.toLocaleString('en-GB', { maximumFractionDigits: 2 })
  }

  async function handleApprove() {
    if (!confirm('Approve this order and send it to Walter Geering?')) return
    setSubmitting(true)
    try {
      const { data, error } = await supabase.rpc('approve_order', { p_token: token, p_approver_email: null })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Approval failed')
      // Fire the WG internal notification now that the order is 'sent'
      try {
        await supabase.functions.invoke('send-order-email', {
          body: {
            order_id: data.order_id,
            client: null,
            internal: {
              subject: `New order ${data.reference} from ${ctx?.site?.name || 'a client'} (approved by ${ctx?.group?.name || 'head office'})`,
              html: `<p><strong>${ctx?.site?.name || 'A site'}</strong> placed order <strong>${data.reference}</strong>, approved by ${ctx?.group?.name || 'head office'}. <a href="${window.location.origin}/orders/${data.order_id}">Open it in admin</a>.</p>`,
            },
          },
        })
      } catch (_) { /* order recorded; WG can still see it in admin */ }
      setOutcome('approved')
    } catch (e) {
      alert(e.message || 'Could not approve — try again in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleReject() {
    if (!reason.trim()) return alert('Please tell the site why you\'re rejecting the order.')
    setSubmitting(true)
    try {
      const { data, error } = await supabase.rpc('reject_order', { p_token: token, p_reason: reason.trim(), p_approver_email: null })
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error || 'Rejection failed')
      setOutcome('rejected')
    } catch (e) {
      alert(e.message || 'Could not reject — try again in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#f6f5f2' }}>
        <div className="text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading order…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#f6f5f2' }}>
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 mx-auto flex items-center justify-center mb-3"><X className="w-7 h-7" /></div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Approval link unavailable</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (outcome === 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#f6f5f2' }}>
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3" style={{ background: brandColour + '20', color: brandColour }}><Check className="w-7 h-7" /></div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Approved</h1>
          <p className="text-sm text-gray-600">Order <strong>{ctx?.order?.reference}</strong> has been sent to Walter Geering. The site placer will be notified.</p>
        </div>
      </div>
    )
  }

  if (outcome === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#f6f5f2' }}>
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-amber-50 text-amber-600 mx-auto flex items-center justify-center mb-3"><X className="w-7 h-7" /></div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Rejected</h1>
          <p className="text-sm text-gray-600">Order <strong>{ctx?.order?.reference}</strong> has been rejected. The site will see your reason and can revise.</p>
        </div>
      </div>
    )
  }

  const o = ctx?.order || {}
  const site = ctx?.site
  const group = ctx?.group

  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: '#f6f5f2' }}>
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 flex items-center gap-4">
          {logoUrl && <img src={logoUrl} alt="" className="h-12 max-w-[160px] object-contain" />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">Order awaiting your approval</div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 truncate">{o.reference}</h1>
            <div className="text-sm text-gray-600">from <strong>{site?.name}</strong> · {group?.name}</div>
          </div>
        </div>

        {/* Summary chips */}
        <div className="bg-white rounded-2xl shadow-sm p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs uppercase text-gray-500">Site</div>
              <div className="font-medium text-gray-800">{site?.name}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs uppercase text-gray-500">Placed</div>
              <div className="font-medium text-gray-800">{fmtDate(o.placed_at)}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Package className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs uppercase text-gray-500">Customer PO</div>
              <div className="font-medium text-gray-800">{o.customer_po || '—'}</div>
            </div>
          </div>
          {(o.delivery_address || o.delivery_postcode) && (
            <div className="flex items-start gap-2 sm:col-span-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase text-gray-500">Delivery</div>
                <div className="font-medium text-gray-800">{[o.delivery_address, o.delivery_postcode].filter(Boolean).join(', ')}</div>
                {o.delivery_date_requested && <div className="text-xs text-gray-600">Requested: {fmtDate(o.delivery_date_requested)}</div>}
              </div>
            </div>
          )}
          {o.client_notes && (
            <div className="sm:col-span-3 pt-2 border-t border-gray-100">
              <div className="text-xs uppercase text-gray-500 mb-1">Notes from the site</div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">{o.client_notes}</div>
            </div>
          )}
        </div>

        {/* Line items */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Order lines</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Product</th>
                  <th className="text-right py-2 w-20">Qty</th>
                  <th className="text-right py-2 w-24">Price</th>
                  <th className="text-right py-2 w-24">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(ctx?.lines || []).map((l, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2">
                      <div className="font-medium text-gray-800">{l.name}</div>
                      {l.sku && <div className="text-xs text-gray-500">{l.sku}</div>}
                    </td>
                    <td className="text-right tabular-nums">{fmtQty(l.qty)}</td>
                    <td className="text-right tabular-nums text-gray-700">{fmtMoney(l.price)}</td>
                    <td className="text-right tabular-nums font-medium text-gray-800">{fmtMoney(l.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-gray-800">
                  <td className="pt-3 text-right pr-3" colSpan={3}>Total</td>
                  <td className="pt-3 text-right tabular-nums">{fmtMoney(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Decision */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          {!showRejectForm ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-base shadow-sm disabled:opacity-50"
                style={{ background: brandColour }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-5 h-5" />}
                Approve and send to Walter Geering
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold disabled:opacity-50"
              >
                <X className="w-5 h-5" /> Reject
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="text-sm font-medium text-gray-800">Reason (the site will see this)</span>
                <textarea
                  className="mt-2 w-full border border-gray-300 rounded-lg p-2 text-sm"
                  rows={3}
                  placeholder="e.g. Please split the toilet-roll order to arrive after the Bank Holiday."
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                />
              </label>
              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={submitting || !reason.trim()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-white font-semibold disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  Send rejection
                </button>
                <button
                  onClick={() => { setShowRejectForm(false); setReason('') }}
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-4">
            This link is single-use and expires on {fmtDate(o.approval_expires_at)}. Approving triggers the order to Walter Geering; rejecting notifies the site with your reason.
          </p>
        </div>
      </div>
    </div>
  )
}
